import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { FileArchive, Shield, LogOut, Loader2, LayoutDashboard, Wrench, CreditCard, Settings, Users, Wallet, BarChart3, ChevronDown, ChevronRight, Download, Tags, BellRing, Trash2 } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'

// Import Tabs
import DashboardTab from '../components/admin/DashboardTab'
import SystemSettingsTab from '../components/admin/SystemSettingsTab'
import ClientReleasesPanel from '../components/admin/ClientReleasesPanel'
import UserReminderContainerTab from '../components/admin/UserReminderContainerTab'
import InvalidUserDeviceLogsTab from '../components/admin/InvalidUserDeviceLogsTab'
import OrderManagementTab from '../components/admin/OrderManagementTab'
import ReportGenerationTab from '../components/admin/ReportGenerationTab'
import ReportingReportsTab from '../components/admin/ReportingReportsTab'
import UserLabelRulesTab from '../components/admin/UserLabelRulesTab'
import UsersTab from '../components/admin/UsersTab'
import LandingDownloadStatsTab from '../components/admin/LandingDownloadStatsTab'
import InstallAndLoginRecordsTab from '../components/admin/InstallAndLoginRecordsTab'
import UserUsageTab from '../components/admin/UserUsageTab'
import UserUninstallTab from '../components/admin/UserUninstallTab'
import UserReminderTab from '../components/admin/UserReminderTab'
import RegistrationUsageSummaryTab from '../components/admin/RegistrationUsageSummaryTab'
import ChannelNewUserStatsTab from '../components/admin/ChannelNewUserStatsTab'
import ChannelConversionStatsTab from '../components/admin/ChannelConversionStatsTab'
import RechargeReportStatsTab from '../components/admin/RechargeReportStatsTab'
import ChannelDetailTab from '../components/admin/ChannelDetailTab'

// 站点品牌标志（与官网首页 BrandLogo 保持一致：优先 logo 图片，否则绿色图标 + 品牌名）
const BRAND_NAME = import.meta.env.VITE_APP_NAME || 'ZipWin'
const BRAND_LOGO = import.meta.env.VITE_APP_LOGO || ''

function Admin() {
  const navigate = useNavigate()
  const { logout } = useAuth()
  const [admin, setAdmin] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('dashboard')
  const [orderManagementInitialSubTab, setOrderManagementInitialSubTab] = useState(null)

  // 后台浏览器标签标题：管理后台 | 品牌名
  useEffect(() => {
    document.title = `管理后台 | ${BRAND_NAME}`
  }, [])

  const menuGroups = [
    {
      title: '概览与财务',
      items: [
        { id: 'dashboard', label: '首页概览', icon: LayoutDashboard },
        { id: 'registration-usage-summary', label: '注册与使用概览', icon: BarChart3 },
        { id: 'channel-new-user-stats', label: '渠道新增用户统计', icon: BarChart3 },
        { id: 'channel-conversion-stats', label: '渠道转化统计', icon: BarChart3 },
        { id: 'order-management', label: '订单管理', icon: CreditCard },
        { id: 'recharge-report-stats', label: '充值报表', icon: BarChart3 },
        { id: 'channel-detail', label: '渠道详情', icon: BarChart3 },
      ]
    },
    {
      title: '推广与客户',
      items: [
        { id: 'users', label: '用户列表', icon: Users },
        { id: 'balance-usage', label: '用户使用情况', icon: Wallet },
        { id: 'user-uninstall', label: '用户卸载情况', icon: Trash2 },
        { id: 'user-reminder', label: '用户弹窗', icon: BellRing },
        { id: 'install-login-records', label: '安装和登录记录', icon: Download },
        { id: 'landing-download-stats', label: '下载统计', icon: Download },
      ]
    },
    {
      title: '系统',
      items: [
        { id: 'settings', label: '系统设置', icon: Settings },
        { id: 'reporting-reports', label: '汇报报表', icon: BarChart3 },
        { id: 'user-label-rules', label: '用户标签规则', icon: Tags },
        { id: 'client-releases', label: '客户端版本', icon: Download },
        { id: 'user-reminder-settings', label: '用户弹窗设置', icon: Settings },
        { id: 'invalid-user-device-logs', label: '异常用户筛选', icon: Users },
        { id: 'report-generation', label: '报表生成(猛男)', icon: Wrench }
      ]
    }
]

  const isPromotionOnlyAdmin = admin?.permission_scope === 'promotion_records_only'
  const visibleMenuGroups = useMemo(() => {
    if (!isPromotionOnlyAdmin) return menuGroups

    const allowedTabIds = new Set(['install-login-records'])
    return menuGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => allowedTabIds.has(item.id))
      }))
      .filter((group) => group.items.length > 0)
  }, [isPromotionOnlyAdmin])

  const visibleTabIds = useMemo(
    () => visibleMenuGroups.flatMap((group) => group.items.map((item) => item.id)),
    [visibleMenuGroups]
  )

  const [expandedGroups, setExpandedGroups] = useState(() => {
    const state = {}
    menuGroups.forEach((g) => {
      state[g.title] = true
    })
    return state
  })

  useEffect(() => {
    checkAdminAuth()
  }, [])

  useEffect(() => {
    if (!admin || visibleTabIds.length === 0) return
    if (!visibleTabIds.includes(activeTab)) {
      setActiveTab(isPromotionOnlyAdmin ? 'install-login-records' : 'dashboard')
    }
  }, [admin, activeTab, isPromotionOnlyAdmin, visibleTabIds])

  const checkAdminAuth = () => {
    const token = localStorage.getItem('token')
    const adminData = localStorage.getItem('admin_user')

    if (!token || !adminData) {
      navigate('/admin/login')
      return
    }

    const user = JSON.parse(adminData)
    if (!user.is_admin) {
      navigate('/admin/login')
      return
    }

    setAdmin(user)
    setLoading(false)
  }

  const handleLogout = () => {
    logout()
    localStorage.removeItem('admin_user')
    navigate('/admin/login')
  }

  const handleSidebarTabClick = (tabId) => {
    if (tabId !== 'order-management') {
      setOrderManagementInitialSubTab(null)
    }
    setActiveTab(tabId)
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    )
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard': return <DashboardTab />
      case 'registration-usage-summary': return <RegistrationUsageSummaryTab />
      case 'channel-new-user-stats': return <ChannelNewUserStatsTab />
      case 'channel-conversion-stats': return <ChannelConversionStatsTab />
      case 'order-management': return <OrderManagementTab initialSubTab={orderManagementInitialSubTab} />
      case 'recharge-report-stats': return <RechargeReportStatsTab />
      case 'channel-detail': return <ChannelDetailTab />
      case 'balance-usage': return <UserUsageTab />
      case 'user-uninstall': return <UserUninstallTab />
      case 'user-reminder': return <UserReminderTab />
      case 'landing-download-stats': return <LandingDownloadStatsTab />
      case 'install-login-records': return <InstallAndLoginRecordsTab />
      case 'users': return <UsersTab />
      case 'report-generation': return <ReportGenerationTab />;
      case 'reporting-reports': return <ReportingReportsTab />;
      case 'user-label-rules': return <UserLabelRulesTab />;
      case 'settings': return <SystemSettingsTab />;
      case 'client-releases': return <ClientReleasesPanel />;
      case 'user-reminder-settings': return <UserReminderContainerTab />;
      case 'invalid-user-device-logs': return <InvalidUserDeviceLogsTab />;
      default: return <DashboardTab />;
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 z-10">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              {/* 站点品牌标志 */}
              <div className="flex items-center gap-2">
                {BRAND_LOGO ? (
                  <img src={BRAND_LOGO} alt={BRAND_NAME} className="w-10 h-10 object-contain rounded-xl" />
                ) : (
                  <span className="flex w-10 h-10 items-center justify-center rounded-xl bg-[#1AB956]">
                    <FileArchive className="w-5 h-5 text-white" />
                  </span>
                )}
                <span className="text-lg font-bold text-gray-900 dark:text-white">{BRAND_NAME}</span>
              </div>
              <div className="h-8 w-px bg-gray-200 dark:bg-gray-600" />
              <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900 dark:text-white">管理后台</h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {admin?.nickname || admin?.username}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              退出
            </button>
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar Navigation */}
        <aside className="w-64 flex-shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
          <nav className="p-4 space-y-6">
            {visibleMenuGroups.map((group, index) => (
              <div key={index} className="space-y-2">
                <button
                  onClick={() => setExpandedGroups((prev) => ({ ...prev, [group.title]: !prev[group.title] }))}
                  className="w-full px-4 py-2 text-sm font-bold text-gray-900 bg-gray-100 rounded-lg uppercase tracking-wider dark:text-gray-100 dark:bg-gray-700/50 flex items-center justify-between"
                >
                  <span>{group.title}</span>
                  {expandedGroups[group.title] ? (
                    <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-300" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-300" />
                  )}
                </button>
                {expandedGroups[group.title] && (
                  <div className="space-y-1">
                    {group.items.map(tab => {
                      const Icon = tab.icon;
                      const isActive = activeTab === tab.id;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => handleSidebarTabClick(tab.id)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-colors ${
                            isActive 
                              ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' 
                              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <Icon className={`w-5 h-5 ${isActive ? 'text-blue-700 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />
                          {tab.label}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </aside>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto p-8">
          <div className="w-full">
            {renderTabContent()}
          </div>
        </main>
      </div>
    </div>
  )
}

export default Admin
